'use client';
import * as React from 'react';
import { Typography, Card } from '@mui/material';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Studysession } from '@/shared/i18n';
import { CreativeRadio } from '@/shared/ui';
import { EventBus } from '@/shared/utils';

import { FormActionButton, OptionSelectedFeedback } from './elements';
import { StudySessionEventKeys, StudySessionEvents } from '../events';
import { StudySessions, StudySessionsOptions } from '../types/study-sessions';

interface StudySessionFormProps {
  eventBus: EventBus<StudySessionEvents>;
  dict: Studysession;
  studySessionsOptions: StudySessionsOptions;
  profileStudySessions: StudySessions[0] | undefined;
}

const StudySessionForm: React.FC<StudySessionFormProps> = ({
  dict,
  eventBus,
  studySessionsOptions,
  profileStudySessions,
}) => {
  const { handleSubmit, control, watch } = useForm<StudySessions[0]>({
    defaultValues: profileStudySessions,
  });

  const watchedValues = useWatch({ control });

  const isDirty = React.useMemo(() => {
    return JSON.stringify(watchedValues) !== JSON.stringify(profileStudySessions);
  }, [watchedValues, profileStudySessions]);

  const onSubmit: SubmitHandler<StudySessions[0]> = React.useCallback(
    (data) => eventBus.emit(StudySessionEventKeys.studySession.update, data),
    [eventBus],
  );

  const descriptionValue = watch('description');

  return (
    <form action="" className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <Card className="bg-paper p-4 md:p-6 lg:p-8 text-center space-y-6">
        <div className="max-w-[700px] m-auto">
          <Typography variant="h5" color="textSecondary">
            {dict.sessions.card_title}
          </Typography>
        </div>

        <div className="flex flex-col justify-center content-center items-center gap-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-[650px] w-full m-auto">
            {studySessionsOptions?.map((sessionOption) => (
              <div key={sessionOption.name}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <CreativeRadio
                      imageSrc={sessionOption.imageSrc}
                      altText={sessionOption.altText}
                      checked={field.value === sessionOption.name}
                      {...field}
                      value={sessionOption.name}
                      name={sessionOption.name}
                    />
                  )}
                />
              </div>
            ))}
          </div>

          {studySessionsOptions && descriptionValue && (
            <OptionSelectedFeedback
              boxClassName="bg-background animate-grow"
              studySessionsOptions={studySessionsOptions}
              selectedStudySession={descriptionValue}
              className="h-[56px]"
            />
          )}
        </div>
      </Card>
      <FormActionButton buttonText={dict.sessions.submit} disabled={!isDirty} />
    </form>
  );
};

export { StudySessionForm };
